import React from "react";
import styles from "../../../../assets/styles/components/Suggestion.module.scss";

// -----------------------------------------------------------------------------
// Types
// -----------------------------------------------------------------------------
export type BiddingStrategyType =
  | "MAXIMIZE_CONVERSIONS"      // コンバージョン最大化
  | "TARGET_CPA"                // 目標コンバージョン単価
  | "MAXIMIZE_CONVERSION_VALUE" // コンバージョン値の最大化
  | "TARGET_ROAS"               // 目標広告費用対効果
  | "MAXIMIZE_CLICKS"           // クリック数最大化
  | "TARGET_SPEND";             // 旧値互換（内部でMAXIMIZE_CLICKS扱い）

export type BiddingSuggestion = {
  campaign_id: string;
  campaign_name: string;
  campaign_type?: string;
  allowed_strategy_types?: string[];
  before_strategy: {
    type: BiddingStrategyType;
    value?: number;
  };
  suggested_strategy: {
    type: BiddingStrategyType;
    value?: number;
  };
  reason?: string;
};

// プルダウンの選択肢定義
const STRATEGY_OPTIONS = [
  { value: "MAXIMIZE_CONVERSIONS", label: "コンバージョン最大化" },
  { value: "TARGET_CPA", label: "目標コンバージョン単価 (tCPA)" },
  { value: "MAXIMIZE_CONVERSION_VALUE", label: "コンバージョン値の最大化" },
  { value: "TARGET_ROAS", label: "目標広告費用対効果 (tROAS)" },
  { value: "MAXIMIZE_CLICKS", label: "クリック数最大化" },
];

type Props = {
  suggestions: BiddingSuggestion[];
  formData: Record<string, { type: BiddingStrategyType; value?: number }>;
  onChange: (id: string, data: { type: BiddingStrategyType; value?: number }) => void;
  unit?: string;
};

// -----------------------------------------------------------------------------
// Component: BiddingStrategyForm
// -----------------------------------------------------------------------------
const BiddingStrategyForm: React.FC<Props> = ({ suggestions, formData, onChange, unit = "円" }) => {

  const getLabel = (type: BiddingStrategyType) =>
    STRATEGY_OPTIONS.find(opt => opt.value === (type === "TARGET_SPEND" ? "MAXIMIZE_CLICKS" : type))?.label || type;

  return (
    <div className={styles.biddingFormContainer}>
      {suggestions.map((sg) => {
        const normalizeType = (t?: string): BiddingStrategyType =>
          (t === "TARGET_SPEND" ? "MAXIMIZE_CLICKS" : (t as BiddingStrategyType));
        const allowedTypes = Array.isArray(sg.allowed_strategy_types) && sg.allowed_strategy_types.length > 0
          ? sg.allowed_strategy_types.map((t) => normalizeType(String(t)))
          : STRATEGY_OPTIONS.map((opt) => normalizeType(opt.value));
        const dedupAllowedTypes = Array.from(new Set(allowedTypes));
        const normalizedSuggestedType = normalizeType(sg.suggested_strategy.type);
        const rawCurrentData = formData[sg.campaign_id] || { type: normalizedSuggestedType };
        const selectedType = dedupAllowedTypes.includes(normalizeType(rawCurrentData.type))
          ? normalizeType(rawCurrentData.type)
          : dedupAllowedTypes[0];
        const currentData = { ...rawCurrentData, type: selectedType };
        const selectableOptions = STRATEGY_OPTIONS.filter((opt) =>
          dedupAllowedTypes.includes(normalizeType(opt.value))
        );

        // 数値入力が必要な戦略か判定
        const needsValue = ["TARGET_CPA", "TARGET_ROAS", "MAXIMIZE_CLICKS"].includes(currentData.type);
        const valueLabel = currentData.type === "TARGET_ROAS" ? "目標値 (%)" : `目標単価 (${unit ?? "円"})`;

        return (
          <section key={sg.campaign_id} className={styles.campaignCard}>
            <div className={styles.campaignName}>{sg.campaign_name}</div>

            {sg.reason && (
              <div className={styles.reasonBar}>
                <strong>Aibex提案理由:</strong> {sg.reason}
              </div>
            )}

            <div className={styles.suggestionBox}>
              {/* Before: 現在の設定 */}
              <div className={styles.beforePanel}>
                <div className={styles.panelTitle}>現在の設定</div>
                <div className={styles.displayRow}>
                  <div className={styles.strategyName}>{getLabel(sg.before_strategy.type)}</div>
                  {sg.before_strategy.value && (
                    <div className={styles.strategyValue}>
                      {sg.before_strategy.value.toLocaleString()}{sg.before_strategy.type === "TARGET_ROAS" ? "%" : unit}
                    </div>
                  )}
                </div>
              </div>

              {/* Arrow */}
              <div className={styles.arrowIcon}>→</div>

              {/* After: 変更後の設定 */}
              <div className={styles.afterPanel}>
                <div className={styles.panelTitle}>AI提案</div>
                <div className={styles.column}>
                  <div className={styles.inputArea}>
                    <select
                      className={styles.selectField}
                      value={currentData.type}
                      onChange={(e) => onChange(sg.campaign_id, { ...currentData, type: normalizeType(e.target.value) })}
                    >
                      {selectableOptions.map(opt => (
                        <option key={opt.value} value={opt.value}>{opt.label}</option>
                      ))}
                    </select>

                    {needsValue && (
                      <div className={styles.subInputWrap}>
                        <span className={styles.subLabel}>{valueLabel}</span>
                        <input
                          type="text"
                          className={styles.smallInput}
                          value={
                            currentData.value !== undefined
                              ? Number(String(currentData.value).replace(/,/g, "")).toLocaleString()
                              : ""
                          }
                          onChange={(e) => {
                            const rawValue = e.target.value.replace(/,/g, "");
                            if (rawValue === "" || !isNaN(Number(rawValue))) {
                              onChange(sg.campaign_id, {
                                ...currentData,
                                value: rawValue === "" ? undefined : Number(rawValue),
                              });
                            }
                          }}
                          placeholder="数値入力"
                        />
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </section>
        );
      })}
    </div>
  );
};

export default BiddingStrategyForm;
